<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { DxPopup } from 'devextreme-vue/popup'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import * as dataGrid from '@/composables/data-grid'
import { getImportResultPage, getResultCell, getResultMessage, useColumns } from '@/composables/registry/import-result'
import { useLookupModelMap } from '@/composables/registry/import-export-lookup'
import ImportFieldValueEditor from '@/components/registry/ImportFieldValueEditor.vue'
import ChipStatus from '@/components/shared/ChipStatus.vue'
import ReferenceLink from '@/components/shared/ReferenceLink.vue'
import { getI18n } from '@/plugins/i18n'
import { useRegistryStore } from '@/stores/registry.store'
import { usePortalStore } from '@/stores/portal.store'
import router, { menuRoutesEnum } from '@/router'

const props = defineProps({ job: { type: Object, required: true } })
const emit = defineEmits(['close'])
const i18n = getI18n()

const registryStore = useRegistryStore()
const portalStore = usePortalStore()

const importStatus = portalStore.importExportStatusEnum
const { getEditorOptionsForField, loadFieldLookupsAsync } = useLookupModelMap()

const loading = ref(true)
const error = ref('')
const run = ref(null)
const gridOptions = ref(null)
const activeStatusChip = ref(null)
const resultFields = ref([])

let resultGrid
let disposed = false

const t = key => i18n.t(`common.importResult.${key}`)
const translateMessage = message => message && i18n.te(message) ? i18n.t(message) : message || ''
const rowMessage = row => getResultMessage(row, resultFields.value)
const counters = ['read_count', 'valid_count', 'created_count', 'updated_count', 'discarded_count']

const existingRecordMode = computed(() => portalStore.getLocalizedImportExistingRecordModes().find(mode => mode.id === run.value?.existing_record_mode)?.name)
const updateMode = computed(() => portalStore.getLocalizedImportUpdateModes().find(mode => mode.id === run.value?.update_mode)?.name)

const statuses = [
  { id: 'ok', color: '#21ba45' },
  { id: 'warning', color: '#b07800' },
  { id: 'error', color: '#c10015' },
  { id: 'skipped', color: '#757575' },
].map(status => ({ ...status, name: t(status.id) }))

const interrupted = computed(() => !!run.value?.finished_at && (
  props.job.status === importStatus.ERROR || (!!run.value.error_message && !run.value.error_count)
))
const warningCount = computed(() => run.value?.warning_count || 0)
const showGrid = computed(() => !!run.value && (run.value.is_dry_run || run.value.error_count > 0 || warningCount.value > 0))
const importCompleted = computed(() => !run.value && !!props.job.last_run_at && ![importStatus.ERROR, importStatus.RUNNING, importStatus.DRY_RUN_RUNNING].includes(props.job.status))
const completionMessage = computed(() => {
  if (props.job.error_message) return t('completedWithErrors')
  if (props.job.warning_message) return t('completedWithWarnings')
  return t('success')
})
const resultTitle = computed(() => {
  if (!run.value?.finished_at) return t('inProgress')
  if (interrupted.value) return t('failed')
  return t(run.value.is_dry_run ? 'simulationCompleted' : 'completed')
})
const summaryChips = computed(() => run.value ? [
  { key: 'ok', count: run.value.is_dry_run ? run.value.valid_count : run.value.imported_count, label: t(run.value.is_dry_run ? 'validRecords' : 'importedRecords') },
  { key: 'warning', count: warningCount.value, label: t('withWarnings') },
  { key: 'error', count: run.value.error_count, label: t('error_count') },
].map(metric => ({ ...metric, color: statuses.find(status => status.id === metric.key).color })) : [])

const filterByStatusChip = metric => {
  if (!resultGrid) return
  const value = resultGrid.columnOption('status', 'filterValue') === metric.key ? null : metric.key
  resultGrid.beginUpdate()
  resultGrid.columnOption('status', 'filterValues', null)
  resultGrid.columnOption('status', 'selectedFilterOperation', '=')
  resultGrid.columnOption('status', 'filterValue', value)
  resultGrid.endUpdate()
  resultGrid.getScrollable()?.scrollTo({ top: 0 })
}
const importedPage = computed(() => {
  if (!run.value?.finished_at || run.value.is_dry_run || !(run.value.imported_count > 0)) return null
  return getImportResultPage(props.job.track_type, portalStore.trackTypeEnum, menuRoutesEnum)
})
const goToImportedPage = async({ targetBlank = false } = {}) => {
  if (!importedPage.value) return
  if (targetBlank) {
    window.open(router.resolve(importedPage.value).href, '_blank')
    return
  }
  const failure = await router.push(importedPage.value)
  if (!failure) emit('close')
}

onMounted(async() => {
  try {
    if (!i18n.te('common.importResult.revalidate') || !i18n.te('error.import.failed')) {
      await portalStore.setLanguageAsync(i18n.locale.value, true)
      if (disposed) return
    }
    const runs = await registryStore.api.getImportExportJobRunsAsync({ $filter: `run_job eq ${props.job.id}`, $orderby: 'id desc', $top: 1 })
    if (disposed) return
    run.value = runs.data[0]
    if (!showGrid.value) return
    const contactCount = Math.max(0, ...(run.value.result_field_keys || []).map(key => Number(/^contact\.(\d+)\./.exec(key)?.[1] || 0)))
    const { data: fields } = await registryStore.api.getImportableFieldsAsync({ track_type: props.job.track_type, contact_count: contactCount })
    if (disposed) return
    resultFields.value = fields
    const activeFields = new Set(run.value.result_field_keys?.length ? run.value.result_field_keys : fields.map(field => field.key))
    await loadFieldLookupsAsync(fields.filter(field => activeFields.has(field.key)))
    if (disposed) return

    gridOptions.value = dataGrid.useDefaultGridOptions({
      dataSource: dataGrid.useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.importExportJobRunRowsUrl, {
        filter: ['run', '=', run.value.id],
        pageSize: 50,
        avoidDuplicateDatasourceIds: true,
      }),
      height: '100%',
      paging: { enabled: true, pageSize: 50 },
      pager: { visible: false },
      scrolling: { mode: 'infinite' },
      focusedRowEnabled: false,
      filterRow: { ...dataGrid.useDefaultFilterRow, visible: true },
      headerFilter: { visible: false },
      columnWidth: 180,
      onOptionChanged: e => {
        if (e.name !== 'columns' || !e.fullName.endsWith('.filterValue')) return
        activeStatusChip.value = e.component.columnOption('status', 'filterValue')
      },
      onInitialized: e => {
        resultGrid = e.component
        dataGrid.useDefaultOnInitialized(e, { enableGrouping: true })
      },
      toolbar: { items: [
        { name: 'columnChooserButton' },
        ...(run.value.is_dry_run && run.value.finished_at ? [{ widget: 'dxButton', location: 'after', options: { text: i18n.t('common.importResult.revalidate'), onClick: async() => {
          await resultGrid.saveEditData()
          await registryStore.api.runImportExportJobAsync(props.job.id, true, true)
          emit('close')
        } } }] : []),
      ] },
      onToolbarPreparing: e => {
        e.toolbarOptions.items.push(dataGrid.toolbarRefreshIcon(e.component))
      },
      columns: useColumns({ fields, activeFields, statuses, getEditorOptionsForField, editable: run.value.is_dry_run }),
      editing: { allowUpdating: run.value.is_dry_run && !!run.value.finished_at, mode: 'cell' },
      onEditingStart: e => { e.cancel = !['error', 'warning'].includes(e.data.status) || !fields.some(field => field.key === e.column.name) },
      onSaving: e => {
        e.cancel = true
        e.promise = Promise.all(e.changes.map(change => registryStore.api.correctImportRowAsync(change.key, change.data.source_row))).then(rows => {
          e.component.cancelEditData()
          rows.forEach(response => e.component.getDataSource().store().push([{ type: 'update', key: response.data.id, data: response.data }]))
        })
      },
      onCellPrepared: e => {
        if (e.rowType !== 'data') return
        const cell = getResultCell(e.data, e.column)
        e.cellElement.classList.toggle('import-result-error', cell?.status === 'error')
        e.cellElement.classList.toggle('import-result-warning', cell?.status === 'warning')
        e.cellElement.title = e.column.dataField === 'message' ? rowMessage(e.data) : translateMessage(cell?.message)
      },
    })
  } catch (e) {
    error.value = e.message || i18n.t('common.error')
  } finally {
    loading.value = false
  }
})

onUnmounted(() => {
  disposed = true
  resultGrid = null
  gridOptions.value?.dataSource.dispose()
})
</script>

<template>
  <dx-popup
    :visible="true"
    :title="i18n.t('common.importExportAction.viewResult')"
    :show-close-button="true"
    :resize-enabled="true"
    :wrapper-attr="{ class: 'import-result-popup' }"
    width="95vw"
    height="90vh"
    @shown="resultGrid?.updateDimensions()"
    @resize="resultGrid?.updateDimensions()"
    @hidden="emit('close')"
  >
    <div class="import-result column no-wrap full-height q-pa-md">
      <q-inner-loading :showing="loading" />
      <div
        v-if="error"
        class="text-negative"
      >
        {{ error }}
      </div>
      <template v-else-if="run">
        <div class="q-mb-xs">
          <div class="text-h6">
            {{ resultTitle }}
          </div>
          <div class="text-caption text-grey-7">
            {{ job.source_file_name }} · {{ t(run.is_dry_run ? 'dryRun' : 'run') }} #{{ run.run_number }}
          </div>
          <div class="row items-center q-gutter-x-md text-body1 q-mt-xs">
            <div
              v-for="metric in summaryChips"
              :key="metric.key"
              class="row items-center no-wrap q-gutter-x-xs"
            >
              <q-chip
                :clickable="showGrid"
                size="sm"
                text-color="white"
                class="q-ma-none text-weight-bold"
                :style="{ backgroundColor: metric.color, boxShadow: activeStatusChip === metric.key ? `0 0 0 2px white, 0 0 0 3px ${metric.color}` : null }"
                :aria-label="`${metric.label}: ${metric.count}`"
                :aria-pressed="activeStatusChip === metric.key"
                @click="filterByStatusChip(metric)"
              >
                {{ metric.count }}
                <q-tooltip>{{ metric.label }}</q-tooltip>
              </q-chip>
            </div>
            <div
              v-if="importedPage"
              class="col-auto"
            >
              <reference-link
                class="import-result-page-link text-caption"
                :text="t('goToPage')"
                :target="importedPage"
                :click-action="goToImportedPage"
              />
            </div>
          </div>
        </div>
        <div
          class="row q-col-gutter-md text-caption text-grey-7 q-mt-sm q-mb-sm"
        >
          <div
            v-for="counter in counters"
            :key="counter"
          >
            {{ t(counter) }}: <strong>{{ run[counter] }}</strong>
          </div>
        </div>
        <div
          v-if="run.duplicate_count > 0"
          class="text-caption text-grey-7 q-mb-sm"
        >
          {{ i18n.t('common.importResult.duplicates', { count: run.duplicate_count }) }} · {{ existingRecordMode }}
          <span v-if="run.existing_record_mode !== portalStore.importExistingRecordModeEnum.NEW_ONLY && updateMode">
            · {{ updateMode }}
          </span>
        </div>
        <div
          v-if="interrupted && run.error_message"
          class="text-caption text-negative q-mb-sm"
        >
          {{ translateMessage(run.error_message) }}
        </div>
        <div
          v-if="showGrid"
          class="col import-result-grid q-mt-md"
        >
          <dx-data-grid
            v-if="gridOptions"
            v-bind="gridOptions"
          >
            <template #correctionEditor="{ data: cellInfo }">
              <import-field-value-editor
                :field="resultFields.find(field => field.key === cellInfo.column.name)"
                :cell-info="{ ...cellInfo, value: Object.prototype.hasOwnProperty.call(cellInfo.data.source_row || {}, cellInfo.column.name) ? cellInfo.data.source_row[cellInfo.column.name] : cellInfo.data.data?.[cellInfo.column.name]?.source_value ?? cellInfo.data.data?.[cellInfo.column.name]?.value }"
              />
            </template>
            <template #resultStatusTemplate="{ data: cellInfo }">
              <chip-status :data="statuses.find(status => status.id === cellInfo.value)" />
            </template>
            <template #resultMessageTemplate="{ data: cellInfo }">
              {{ rowMessage(cellInfo.data) }}
            </template>
          </dx-data-grid>
        </div>
      </template>
      <div
        v-else-if="!loading && importCompleted"
        class="text-body1"
      >
        {{ completionMessage }}
      </div>
      <div v-else-if="!loading">
        {{ t('noRuns') }}
      </div>
    </div>
  </dx-popup>
</template>

<style scoped>
:global(.import-result-popup .dx-popup-content) {
  max-height: none !important;
  min-height: 0;
}

.import-result-grid {
  min-height: 0;
}

.import-result-page-link :deep(.external-link) {
  font-size: 12px !important;
  width: 12px;
}

:deep(.import-result-error) {
  background-color: #ffebee;
  color: #b71c1c;
}

:deep(.import-result-warning) {
  background-color: #fff8e1;
  color: #795548;
}

:deep(.dx-datagrid-rowsview .dx-row > td) {
  border-right-color: #eeeeee;
  font-weight: normal;
}
</style>
